import { useIdentityVerification } from "@/api/auth/useIdentityVerification";
import { UserSummaryDto } from "@/api/user/dto";
import { useCheckDuplicateEmail } from "@/api/user/useCheckDuplicateEmail";
import { useSignupWithEmail } from "@/api/user/useSignupWithEmail";
import { useSignupWithKakao } from "@/api/user/useSignupWithKakao";
import { useAuth } from "@/auth/AuthContext";
import { usePostHogQuestionnaireTracking } from "@/hooks/usePostHogQuestionnaireTracking";
import { useQuestionnaireFlow } from "@/hooks/useQuestionnaireFlow";
import { QuestionnaireFlowQuestion, QuestionnaireFlowQuestionOption } from "@/types/question.types";
import { Anchor, Button, Center, Stack, Text } from "@mantine/core";
import { isEmail, isNotEmpty, useForm } from "@mantine/form";
import { useTimeout } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { usePostHog } from "posthog-js/react";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { QuestionAndAnswerResolver } from "../PersonalityTest/QuestionAndAnswerResolver";
import { QuestionnaireUILayout } from "../shared/QuestionnaireUILayout";

export interface SignupFormValues {
  nickname: string;
  email: string;
  password: string;
  passwordConfirmation: string;
  identityVerificationId: string;
}

interface SignupQuestionnaireProps {
  questions: QuestionnaireFlowQuestion[];
  userSummary?: UserSummaryDto;
  isKakaoSignup?: boolean;
}

export function SignupQuestionnaire({
  questions,
  userSummary,
  isKakaoSignup = false,
}: SignupQuestionnaireProps) {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const { signupWithEmail } = useAuth();
  const { mutateAsync: signUpWithEmail, isPending, isError } = useSignupWithEmail();
  const {
    mutateAsync: signUpWithKakao,
    isPending: isSigningUpWithKakao,
    isError: isErrorSigningUpWithKakao,
  } = useSignupWithKakao();
  const { mutateAsync: checkDuplicateEmail, isPending: isCheckingEmail } = useCheckDuplicateEmail();
  const { requestVerification, isLoading: isVerifying } = useIdentityVerification();
  const posthog = usePostHog();

  const { trackFlowStarted, trackQuestionViewed, trackQuestionAnswered, trackFlowCompleted } =
    usePostHogQuestionnaireTracking();

  useEffect(() => {
    trackFlowStarted("signup_onboarding");
  }, []);

  const form = useForm<SignupFormValues>({
    initialValues: {
      nickname: "",
      email: "",
      password: "",
      passwordConfirmation: "",
      identityVerificationId: "",
    },
    validate: {
      nickname: isNotEmpty("닉네임을 입력해주세요."),
      identityVerificationId: isNotEmpty("본인인증을 완료해주세요."),
      email: isKakaoSignup ? undefined : isEmail("이메일 형식이 올바르지 않습니다."),
      password: isKakaoSignup
        ? undefined
        : (value, values) => {
            if (value.length < 8) return "비밀번호는 8자 이상 입력해주세요.";
            if (value !== values.passwordConfirmation) return "비밀번호가 일치하지 않습니다.";
            return null;
          },
    },
    validateInputOnChange: true,
  });

  const getQuestionPaths = (q: QuestionnaireFlowQuestion) => ({
    fieldPath: q.slug === "identityVerification" ? "identityVerificationId" : q.slug,
    validationPath: q.slug === "identityVerification" ? "identityVerificationId" : q.slug,
  });

  const {
    currentQuestion,
    questionAnalysis,
    isLastStep,
    progressValue,
    currentStep,
    actions: { goToNextStep, goToPreviousStep },
  } = useQuestionnaireFlow({ questions, getQuestionPaths });

  useEffect(() => {
    if (currentQuestion) {
      trackQuestionViewed("signup_onboarding", currentQuestion, currentStep, questions.length);
    }
  }, [currentQuestion?.slug]);

  const { start: goToNextStepAfterDelay, clear: clearGoToNextStepDelay } = useTimeout(
    goToNextStep,
    300
  );

  const handleGoToPreviousStep = () => {
    clearGoToNextStepDelay();
    goToPreviousStep();
  };

  const handleIdentityVerification = async () => {
    try {
      const { identityVerificationId } = await requestVerification();
      form.setFieldValue("identityVerificationId", identityVerificationId);
      posthog.capture("user_completes_identity_verification", {
        identityVerificationId,
        method: isKakaoSignup ? "kakao" : "email",
      });
      goToNextStep();
    } catch (e) {
      const err = e as Error;
      if (err.message === "CANCELLED") return;
      notifications.show({
        message: err.message,
        color: "red",
      });
    }
  };

  const handleNextClick = async () => {
    if (currentQuestion) {
      trackQuestionAnswered("signup_onboarding", currentQuestion, currentStep);
    }

    if (currentQuestion?.slug === "email") {
      const { isDuplicate } = await checkDuplicateEmail({ email: form.values.email });
      if (isDuplicate) {
        form.setFieldError(
          "email",
          <Text size="sm" c="red">
            이미 사용 중인 이메일입니다.{" "}
            <Anchor size="sm" fw={600} onClick={() => navigate("/login")}>
              로그인 페이지로 이동
            </Anchor>
          </Text>,
        );
        return;
      }
      goToNextStep();
    } else {
      goToNextStep();
    }
  };

  const handleFormSubmit = async (values: SignupFormValues) => {
    try {
      if (isKakaoSignup) {
        await signUpWithKakao({
          nickname: values.nickname,
          identityVerificationId: values.identityVerificationId,
        });
      } else {
        await signupWithEmail({
          email: values.email,
          nickname: values.nickname,
          password: values.password,
          identityVerificationId: values.identityVerificationId,
        });
      }

      await trackFlowCompleted("signup_onboarding", questions.length);

      if (userSummary?.hasCompletedPersonalityTest) {
        navigate("/home");
      } else {
        navigate("/personality-test");
      }
    } catch (error) {
      console.error("Signup failed:", error);
      const message =
        error instanceof Error && error.message
          ? error.message
          : "회원가입에 실패했습니다. 다시 시도해주세요.";
      notifications.show({
        title: "오류",
        message,
        color: "red",
      });
    }
  };

  const handleSelect = (_option: QuestionnaireFlowQuestionOption) => {};

  if (!currentQuestion || !questionAnalysis) {
    return (
      <Center h="100%">
        <Text>Loading questions...</Text>
      </Center>
    );
  }

  const isIdentityVerificationStep = currentQuestion.slug === "identityVerification";
  const isVerified = form.values.identityVerificationId !== "";

  const isNextButtonDisabled = (() => {
    if (isIdentityVerificationStep) return true;
    return !form.isValid(currentQuestion.validationPath) || isCheckingEmail;
  })();

  const questionAreaContent = isIdentityVerificationStep ? (
    <Stack align="center" gap="md">
      {isVerified ? (
        <Text c="green" fw={600}>
          ✓ 본인인증이 완료되었습니다
        </Text>
      ) : (
        <Button
          size="lg"
          onClick={handleIdentityVerification}
          loading={isVerifying}
          fullWidth
        >
          본인인증 / 성인인증 하기
        </Button>
      )}
    </Stack>
  ) : (
    <QuestionAndAnswerResolver
      currentQuestion={currentQuestion}
      form={form}
      fieldPath={currentQuestion.fieldPath}
      handleSelectMultiple={handleSelect}
      handleSelectSingle={handleSelect}
      handleSelectRange={() => {}}
    />
  );

  return (
    <QuestionnaireUILayout
      progressValue={progressValue}
      showBackButton={currentQuestion.slug !== "identityVerification"}
      onBackButtonClick={handleGoToPreviousStep}
      title={currentQuestion.title}
      subtitle={currentQuestion.subtitle ?? undefined}
      questionAreaContent={questionAreaContent}
      showNextButton={!isLastStep && !isIdentityVerificationStep}
      onNextButtonClick={handleNextClick}
      isNextButtonDisabled={isNextButtonDisabled}
      showSubmitButton={isLastStep}
      onSubmitButtonClick={() => handleFormSubmit(form.values)}
      isSubmitButtonDisabled={!form.isValid() || isPending}
      isErrorState={isError || isErrorSigningUpWithKakao}
      pbForQuestionTitle={Boolean(currentQuestion?.extra?.options?.length ?? 0 > 6) ? "sm" : "xl"}
      nextButtonText="다음"
      isNextButtonLoading={isCheckingEmail}
      isSubmitButtonLoading={isPending || isSigningUpWithKakao}
    />
  );
}
